import React from 'react'
import { Box } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import * as Styled from './styles'

type StepState = 'pending' | 'active' | 'complete' | 'failed'
type StatusType = 'success' | 'failure'

export interface StepProgress {
  state: StepState
  status?: StatusType
  completedAt?: Date
}

interface StepInfo {
  label: string
  color: string
}

interface SyncProgressBarProps {
  steps?: StepInfo[]
  progress: StepProgress[]
  isSyncing: boolean
  showIcons?: boolean
  elapsedTime?: string
}

const defaultSteps: StepInfo[] = [
  { label: 'Import', color: '#32965D' },
  { label: 'Ingest', color: '#1B51A4' },
  { label: 'Publish', color: '#2E7DAB' },
  { label: '3rd Party Processing', color: '#FBC02D' },
]

// Helper to format elapsed time in Xm Ys
const formatElapsedTime = (ms: number) => {
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}m ${seconds}s`
}

const SyncProgressBar: React.FC<SyncProgressBarProps> = ({
  steps = defaultSteps,
  progress,
  isSyncing,
  showIcons = false,
  elapsedTime: externalElapsedTime,
}) => {
  // Show component when syncing OR when there's completed progress to display
  const hasCompletedProgress = progress.some(step => step.state === 'complete' || step.state === 'failed')
  if (!isSyncing && !hasCompletedProgress) return null

  // Get first and last completed steps with valid completedAt for time calculation
  const completedSteps = progress.filter((p) => p.completedAt)
  const firstCompletedAt = completedSteps[0]?.completedAt
  const lastStepIndex = steps.length - 1
  const lastStepProgress = progress[lastStepIndex]

  // Use external elapsed time if provided, otherwise calculate from timestamps
  let elapsedTime = externalElapsedTime || ''
  if (!externalElapsedTime && firstCompletedAt && lastStepProgress?.completedAt) {
    const diff = lastStepProgress.completedAt.getTime() - firstCompletedAt.getTime()
    elapsedTime = formatElapsedTime(diff)
  }

  return (
    <Box sx={{ width: '100%', paddingBottom: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {steps.map(({ label, color }, idx) => {
          const { state } = progress[idx] || { state: 'pending' }

          let icon = null
          if (showIcons) {
            if (state === 'complete') {
              icon = <CheckCircleIcon data-testid='check-icon' fontSize='small' sx={{ color: '#4caf50' }} />
            } else if (state === 'failed') {
              icon = <ErrorIcon data-testid='error-icon' fontSize='small' sx={{ color: '#f44336' }} />
            } else if (state === 'active') {
              icon = (
                <Box
                  data-testid='active-dot'
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    backgroundColor: color,
                    border: '2px solid white',
                  }}
                />
              )
            }
          }

          let bgColor = '#ddd'
          if (state === 'complete' || state === 'active') bgColor = color
          else if (state === 'failed') bgColor = '#f44336'

          return (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                mx: idx === 0 ? 0 : 0.5,
              }}
            >
              {/* Label and Icon */}
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems:
                    idx === 0
                      ? 'flex-start'
                      : idx === steps.length - 1
                      ? 'flex-end'
                      : 'center',
                  mb: 0.5,
                }}
              >
                <Styled.StepLabel>{label}</Styled.StepLabel>
                {icon && (
                  <Box mt={0.5}>
                    {icon}
                  </Box>
                )}
              </Box>

              {/* Bar */}
              <Box
                sx={{
                  height: state === 'pending' ? 1 : 2,
                  backgroundColor: state === 'pending' ? '#D7D7D7' : bgColor,
                  borderRadius: 1,
                  transition: 'background-color 0.3s ease, height 0.3s ease',
                  mt: 0.5,
                  position: 'relative',
                }}
              />
            </Box>
          )
        })}
      </Box>

      {/* Show final status and elapsed time below last bar */}
      {(lastStepProgress.state === 'complete' || lastStepProgress.state === 'failed') && lastStepProgress.status && (
        <Box mt={2} sx={{ textAlign: 'right', justifyItems: 'flex-end' }}>
          <Styled.StepLabel>
            Final Status:{' '}
            <strong
              style={{
                color: lastStepProgress.status === 'success' ? 'green' : 'red',
              }}
            >
              {lastStepProgress.status.toUpperCase()}
            </strong>
          </Styled.StepLabel>
          {elapsedTime && (
            <Styled.StepLabel>Time Elapsed: {elapsedTime}</Styled.StepLabel>
          )}
        </Box>
      )}
    </Box>
  )
}

export default SyncProgressBar